import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { setScrollbarWidthProperty } from '../utils/style-helpers'
import { fetchPage as fetchPageApi } from '../utils/api'
import { useConfigStore } from './config'

interface PageData {
  header?: any,
  body?: any,
  pageMeta?: any,
  metaData?: any,
  footer?: any
}

interface SetPageParams {
  pageData: PageData,
  brand: string,
  status: number,
  imageBrand: string
}

interface FetchPageContext {
  url: string,
  brand: string,
  sweepstakesActive?: boolean,
  token?: string
}

export const usePageStore = defineStore('page', () => {
  // Stores
  const configStore = useConfigStore()

  // State
  const pageData = ref<PageData>({})
  const brand = ref<string>('')
  const imageBrand = ref<string>('')
  const status = ref<number | null>(null)
  const csLoaded = ref<boolean>(false)
  const isPrintPreview = ref<boolean>(false)
  const viewportMetaModified = ref<boolean>(false)
  const affiliateDisclaimerOpen = ref<boolean>(false)
  const isHome = ref<boolean>(false)

  // Actions
  const fetchPage = async (context: FetchPageContext): Promise<void> => {
    // TODO: Defer setting to avoid flashing loading on every page change?
    const proxyServiceURL = typeof window !== 'undefined' ? configStore.rsConfig.proxyServiceUrl : undefined
    const sweepstakesActive = !!(configStore?.rsConfig.sweepstakesActive)
    const response = await fetchPageApi({
      ...context,
      sweepstakesActive
    }, proxyServiceURL)
    setPage({
      pageData: response.pageData,
      brand: response.brand,
      status: response.status,
      imageBrand: response.imageBrand
    })
  }

  const appMounted = async (): Promise<void> => {
    setScrollbarWidthProperty()
  }

  const hasPreviewToken = computed((): boolean => {
    return !import.meta.env.SSR && /[?&]previewToken=(?:\S)/.test(window.location.search)
  })

  const setClientScriptsLoaded = (): void => {
    csLoaded.value = true
  }

  const setViewportMetaModified = (newValue: boolean): void => {
    viewportMetaModified.value = newValue
  }

  const setPage = (newPage: SetPageParams): void => {
    pageData.value = newPage.pageData
    brand.value = newPage.brand
    imageBrand.value = newPage.imageBrand
    status.value = newPage.status
  }

  const setStatus = (newStatus: number): void => {
    status.value = newStatus
  }

  const setPrintPreview = (newValue: boolean): void => {
    isPrintPreview.value = newValue
  }

  const toggleAffiliateDisclaimer = (): void => {
    affiliateDisclaimerOpen.value = !affiliateDisclaimerOpen.value
  }

  const setIsHome = (newValue: boolean): void => {
    isHome.value = newValue
  }

  return {
    pageData,
    brandFromStore: brand,
    imageBrand,
    csLoaded,
    viewportMetaModified,
    isPrintPreview,
    hasPreviewToken,
    status,
    affiliateDisclaimerOpen,
    isHome,
    fetchPage,
    appMounted,
    setClientScriptsLoaded,
    setPage,
    setViewportMetaModified,
    setStatus,
    setPrintPreview,
    toggleAffiliateDisclaimer,
    setIsHome
  }
})
